import React, { useEffect, useRef, useState, useCallback } from 'react';
import { TrashIcon, CopyIcon, FilmSlateIcon, TextTIcon, UserIcon, ChatCircleIcon, BracketsRoundIcon, ArrowLineRightIcon, Image as ImageIcon, CheckSquareIcon, UsersIcon } from '@phosphor-icons/react';
import { BlockType } from './SlashMenu';

interface BlockOptionsMenuProps {
    position: { top: number; left: number };
    currentType: BlockType;
    onClose: () => void;
    onDelete: () => void;
    onDuplicate: () => void;
    onTypeChange: (type: BlockType) => void;
    onMultiSelect?: () => void;
}

export const BlockOptionsMenu: React.FC<BlockOptionsMenuProps> = ({
    position,
    currentType,
    onClose,
    onDelete,
    onDuplicate,
    onTypeChange,
    onMultiSelect
}) => {
    const menuRef = useRef<HTMLDivElement>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [showTypeSubmenu, setShowTypeSubmenu] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose]);

    // Keyboard navigation
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        const maxIndex = onMultiSelect ? 3 : 2; // Delete, MultiSelect?, Duplicate, Turn Into

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex(prev => prev < maxIndex ? prev + 1 : 0);
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex(prev => prev > 0 ? prev - 1 : maxIndex);
                break;
            case 'Enter':
                e.preventDefault();
                // Handle selection based on index
                if (selectedIndex === 0) { onDelete(); onClose(); }
                else if (selectedIndex === 1 && onMultiSelect) { onMultiSelect(); onClose(); }
                else if (selectedIndex === (onMultiSelect ? 2 : 1)) { onDuplicate(); onClose(); }
                else { setShowTypeSubmenu(true); }
                break;
            case 'Escape':
                e.preventDefault();
                onClose();
                break;
        }
    }, [selectedIndex, onDelete, onDuplicate, onMultiSelect, onClose]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    // Prevent menu from going off screen
    const style: React.CSSProperties = {
        top: position.top,
        left: position.left,
    };

    const types = [
        { type: 'scene-heading', label: 'Scene Heading', icon: FilmSlateIcon },
        { type: 'action', label: 'Action', icon: TextTIcon },
        { type: 'character', label: 'Character', icon: UserIcon },
        { type: 'dialogue', label: 'Dialogue', icon: ChatCircleIcon },
        { type: 'parenthetical', label: 'Parenthetical', icon: BracketsRoundIcon },
        { type: 'transition', label: 'Transition', icon: ArrowLineRightIcon },
        { type: 'shot', label: 'Shot', icon: ImageIcon },
        { type: 'dual-dialogue', label: 'Dual Dialogue', icon: UsersIcon },
    ];

    return (
        <div
            ref={menuRef}
            className="fixed z-[9999] w-56 bg-zinc-900 border border-white/10 rounded-lg shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-100 py-1"
            style={style}
            onClick={(e) => e.stopPropagation()}
        >
            <div className="px-2 py-1.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest border-b border-white/5 mb-1">
                Actions
            </div>

            <button
                onClick={() => { onDelete(); onClose(); }}
                onMouseEnter={() => setSelectedIndex(0)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                    selectedIndex === 0 ? 'bg-red-500/20 text-red-300' : 'text-red-400 hover:bg-red-500/10'
                }`}
            >
                <TrashIcon className="w-4 h-4" />
                <span>Delete</span>
                {selectedIndex === 0 && <kbd className="ml-auto text-[9px] px-1 py-0.5 bg-zinc-800 rounded">↵</kbd>}
            </button>

            {onMultiSelect && (
                <button
                    onClick={() => { onMultiSelect(); onClose(); }}
                    onMouseEnter={() => setSelectedIndex(1)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                        selectedIndex === 1 ? 'bg-indigo-500/20 text-indigo-300' : 'text-indigo-400 hover:bg-indigo-500/10'
                    }`}
                >
                    <CheckSquareIcon className="w-4 h-4" />
                    <span>Multi-Select</span>
                    {selectedIndex === 1 && <kbd className="ml-auto text-[9px] px-1 py-0.5 bg-zinc-800 rounded">↵</kbd>}
                </button>
            )}

            <button
                onClick={() => { onDuplicate(); onClose(); }}
                onMouseEnter={() => setSelectedIndex(onMultiSelect ? 2 : 1)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                    selectedIndex === (onMultiSelect ? 2 : 1) ? 'bg-white/10 text-white' : 'text-zinc-300 hover:bg-white/5'
                }`}
            >
                <CopyIcon className="w-4 h-4" />
                <span>Duplicate</span>
                {selectedIndex === (onMultiSelect ? 2 : 1) && <kbd className="ml-auto text-[9px] px-1 py-0.5 bg-zinc-800 rounded">↵</kbd>}
            </button>

            <div className="px-2 py-1.5 text-[10px] font-bold text-zinc-500 uppercase tracking-widest border-b border-white/5 border-t mt-1 mb-1 bg-white/[0.02]">
                Turn Into
            </div>

            <div className="max-h-48 overflow-y-auto">
                {types.map((item) => (
                    <button
                        key={item.type}
                        onClick={() => { onTypeChange(item.type as BlockType); onClose(); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${currentType === item.type ? 'bg-indigo-500/10 text-indigo-400' : 'text-zinc-400 hover:bg-white/5 hover:text-zinc-200'}`}
                    >
                        <item.icon className="w-4 h-4" />
                        <span>{item.label}</span>
                        {currentType === item.type && <span className="ml-auto text-[10px] opacity-50">Current</span>}
                    </button>
                ))}
            </div>
        </div>
    );
};
